import React, { ReactElement } from "react";
import {
    Form,
    useListRequestTypesQuery,
} from "../../../__generated__/api";
import { Filters } from "../../filters/Filters";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface RequestTypesPickerProps<TIsMulti = false, ValueIdType = TIsMulti extends true ? string[] : string> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    shouldShowDropdown?: boolean;
    className?: string;
    toggleClassName?: string;
    toggleText?: string
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (sourceId: ValueIdType, texts: ValueIdType) => void;
}

const RequestTypes = React.forwardRef(<TIsMulti extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, shouldShowDropdown, toggleText, toggleClassName,
}: RequestTypesPickerProps<TIsMulti>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useListRequestTypesQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<Form>[] : Option<Form>;
    type ValueType = TIsMulti extends true ? Option<Form>[] : Option<Form>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<Form>).value) as Parameters<typeof onSelected>[0];
        const texts = (Array.isArray(selected)
            ? selected.map(row => row.label as string)
            : (selected as Option<Form>).label) as Parameters<typeof onSelected>[0];
        onSelected(values, texts);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.forms?.map(form => ({
        label: form.name as string,
        value: form.id.toString() as string,
        data: form,
    })) ?? [];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]).filterNull() : valueMap[value as string];
    if (shouldShowDropdown) {
        return (
            <Filters
                toggleText={toggleText}
                isMulti={isMulti}
                categories={values.map(row => ({
                    id: row.value,
                    name: row.label as string,
                }))}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={
                    (isMulti ? new Set(value) : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => (isMulti
                    ? (onSelected as ((value: string[]) => void))(Array.from(current))
                    : (onSelected as ((value: string) => void))(current as string))}
            />
        );
    }
    return (
        <Select
            ref={ref}
            isSortable
            className={className}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={(values ?? []) as Option<Form>[]}
            value={selected as ValueType}
        />
    );
});

RequestTypes.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    shouldShowDropdown: undefined,
    className: "",
    toggleClassName: "",
    toggleText: undefined,
};

export default RequestTypes as <
    TMulti extends boolean = false,
> (
    props: RequestTypesPickerProps<TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof RequestTypes>;
